import { useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import * as posta from '../../servizi/posta.ts'
import { useSessione } from '../../servizi/sessione.tsx'
import { Avviso } from '../../componenti/Pezzi.tsx'
import { Bottone, Modale } from '../../componenti/base.tsx'
import { Compositore, type AperturaCompositore } from '../posta/Compositore.tsx'

/**
 * «Scrivi email» dalla scheda (decisione di Davide del 2/11/2026): il compositore della webmail, senza
 * passare dalla Posta. Destinatario l'email della persona, se c'e'; casella la propria, o un'altra fra
 * quelle da cui si scrive; firma come in Posta (508). Dopo l'invio la conversazione si aggancia alla
 * scheda (POST /posta/thread/:id/aggancia, con i suoi controlli di permesso e perimetro), e le risposte
 * della persona arrivano nella stessa conversazione.
 *
 * Il comando c'e' solo per chi usa la posta e aggancia le conversazioni alle schede, e mai in palestra
 * (490), dove la posta non si usa. Il vecchio invio dalla scheda con Scaleway TEM (POST /email) resta
 * com'era per chi lo chiama, ma l'interfaccia non lo usa.
 */
export function ScriviEmail({ personaId, email }: { personaId: string; email: string | null }) {
  const { puo, io } = useSessione()
  const [apertura, setApertura] = useState<{ caselle: posta.CasellaWebmail[]; firme: posta.FirmeDellaPersona | null
    bozza: AperturaCompositore } | null>(null)
  const [carico, setCarico] = useState(false)
  const [esito, setEsito] = useState<{ tipo: 'successo' | 'attenzione' | 'errore'; testo: string } | null>(null)

  if (!puo('posta.usa') || !puo('posta.aggancia') || io?.palestra?.accesa) return null

  async function apri() {
    setCarico(true); setEsito(null)
    try {
      const [tutte, firme] = await Promise.all([posta.elencaCaselle(), posta.elencaFirme().catch(() => null)])
      const caselle = tutte.filter((c) => c.mia)
      // Mai da una casella che non spedisce (3/11/2026): spenta, o senza server di invio.
      const mittenti = caselle.filter((c) => c.spedisce !== false)
      const da = mittenti.find((c) => c.ambito === 'utente') ?? mittenti[0]
      if (!da) {
        setEsito({ tipo: 'attenzione', testo: 'Non hai una casella da cui scrivere: la configura chi gestisce gli utenti.' })
        return
      }
      setApertura({ caselle, firme, bozza: { nuova: {
        casella_id: da.id, a: email ?? '', cc: '', ccn: '', oggetto: '',
        testo: posta.testoConFirma(posta.firmaPer(firme, da.id)),
      } } })
    } catch (e) {
      setEsito({ tipo: 'errore', testo: e instanceof ErroreApi ? e.message : 'La posta non risponde: riprova fra poco.' })
    } finally { setCarico(false) }
  }

  /**
   * Invio non riuscito (3/11/2026): il compositore resta aperto con il motivo, e il messaggio resta uno solo
   * nelle «Bozze» della casella. La sua conversazione c'e' gia': si collega subito alla scheda, come prima, e le
   * risposte arriveranno li' quando partira'.
   */
  function nonInviato(r: { thread_id?: string }) {
    if (r.thread_id) void posta.agganciaThread(r.thread_id, { persona_id: personaId }).catch(() => undefined)
  }

  async function spedito(r?: { thread_id?: string; stato_invio?: string }) {
    setApertura(null)
    // Se il server di posta rifiuta, il messaggio resta nella Posta con l'errore: si collega lo stesso.
    const partita = r?.stato_invio !== 'errore'
    const inizio = partita ? 'Email inviata' : 'Il server di posta non ha accettato il messaggio, che resta nella Posta con l’errore'
    if (!r?.thread_id) {
      setEsito({ tipo: 'attenzione', testo: `${inizio}. Il collegamento alla scheda si fa dalla Posta.` })
      return
    }
    try {
      await posta.agganciaThread(r.thread_id, { persona_id: personaId })
      setEsito(partita
        ? { tipo: 'successo', testo: 'Email inviata e collegata alla scheda. In «Posta» la conversazione porta '
            + 'l’etichetta «scheda»; le risposte arrivano lì.' }
        : { tipo: 'errore', testo: `${inizio}. La conversazione è collegata alla scheda.` })
    } catch (e) {
      setEsito({ tipo: partita ? 'attenzione' : 'errore', testo: `${inizio}. Il collegamento alla scheda non è riuscito: ${
        e instanceof ErroreApi ? e.message : 'riprova dalla Posta con «Collega a una scheda».'}` })
    }
  }

  return (
    <>
      <Bottone variante="secondario" inCorso={carico} onClick={() => void apri()}
               title={email ? `Scrivi a ${email} dalla tua casella` : 'Scrivi un’email dalla tua casella, collegata a questa scheda'}>
        Scrivi email
      </Bottone>
      {/* L'esito in una finestra: il comando sta fra i comandi della scheda, dove un avviso non ha posto. */}
      {esito && (
        <Modale titolo={esito.tipo === 'successo' ? 'Email inviata' : 'Scrivi email'} chiudi={() => setEsito(null)}
          azioni={<Bottone variante="primario" onClick={() => setEsito(null)}>Chiudi</Bottone>}>
          <Avviso tipo={esito.tipo}>{esito.testo}</Avviso>
        </Modale>
      )}
      {apertura && (
        <Compositore key="scheda" caselle={apertura.caselle} apertura={apertura.bozza} firme={apertura.firme}
                     chiudi={() => setApertura(null)} spedito={(r) => void spedito(r)} nonInviato={nonInviato} />
      )}
    </>
  )
}
